import * as React from 'react';
import {inject, observer} from 'mobx-react';
import {FilterStore, UserStore} from '../../../stores';
import {BaseRouteProps} from '../../../interfaces';
import {ReportDataService, StyleDataService, SurveyDataService, CharacteristicGroupDataService, SurveyReportTypeDataService} from '../../../services';
import {LoadingIndicator} from '../../loadingIndicator/LoadingIndicator';
import {exportDetailedPages, PdfDetailed} from "./pdf_detailed";
import {Style, CharacteristicGroup, Characteristic, SurveyReportType} from "../../../models";

interface PdfDetailedPreviewProps extends BaseRouteProps<{}> {
    UserStore?: UserStore;
    FilterStore?: FilterStore;
}

interface PdfDetailedPreviewState {
    loading: boolean;
    selectedUserData: any;
    allStyles: Style[];
    surveyTexts?: any;
    characteristicGroups: { [id: number]: CharacteristicGroup };
    characteristics: { [id: number]: Characteristic };
    surveyReportTypes: SurveyReportType[];
}

@inject('UserStore', 'FilterStore')
@observer
export class PdfDetailedPreview extends React.Component<PdfDetailedPreviewProps, PdfDetailedPreviewState> {
    private reportDataService = new ReportDataService(this.props.notificationService);
    private styleDataService = new StyleDataService(this.props.notificationService);
    private characteristicGroupDataService = new CharacteristicGroupDataService(this.props.notificationService);
    private surveyReportTypeDataService = new SurveyReportTypeDataService(this.props.notificationService);

    constructor(props: PdfDetailedPreviewProps) {
        super(props);
        this.state = {
            loading: true,
            selectedUserData: null,
            allStyles: [],
            characteristicGroups: {},
            characteristics: {},
            surveyReportTypes: []
        };
    }

    public async componentDidMount() {
        const user = this.props.UserStore && this.props.UserStore.userInfo;
        const loggedInUserId = user ? user.id : undefined;
        const surveyVersion = this.props.FilterStore && this.props.FilterStore.surveyVersion;
        const survey = await (new SurveyDataService(this.props.notificationService).get(this.props.FilterStore!.survey!.id));
        const surveyTexts = survey.surveyTexts;
        await this.setState({ surveyTexts });

        if (loggedInUserId && surveyVersion) {
            Promise.all([
                this.reportDataService.getIndividualReportDataLatestBySurveyVersion(loggedInUserId, surveyVersion.id),
                this.styleDataService.getAllBySurveyVersionId(surveyVersion.id),
                this.surveyReportTypeDataService.getBySurveyVersion(surveyVersion.id)
            ]).then(([selectedUserData, allStyles, surveyReportTypes]) => {
                this.setState({ selectedUserData, allStyles, surveyReportTypes, loading: false });
                this.loadCharacteristicGroups(selectedUserData);
            }).catch(() => {
                this.setState({ loading: false });
            });
        } else if (loggedInUserId) {
            this.reportDataService.getIndividualReportDataLatest(loggedInUserId)
                .then((selectedUserData) => {
                    if (selectedUserData && selectedUserData.style && selectedUserData.style.surveyVersionID) {
                        this.styleDataService.getAllBySurveyVersionId(selectedUserData.style.surveyVersionID)
                            .then((allStyles) => {
                                this.setState({ selectedUserData, allStyles, loading: false });
                            })
                            .catch(() => {
                                this.setState({ selectedUserData, loading: false });
                            });
                    } else {
                        this.setState({ selectedUserData, loading: false });
                    }
                })
                .catch(() => {
                    this.setState({ loading: false });
                });
        } else {
            this.setState({ loading: false });
        }
    }

    private loadCharacteristicGroups(reportData: any) {
        if (reportData && reportData.inclinationGroups) {
            reportData.inclinationGroups.forEach((inclinationGroup: any) => {
                const id = inclinationGroup.characteristicGroupID;
                if (id && !this.state.characteristicGroups[id]) {
                    this.characteristicGroupDataService.get(id).then((characteristicGroup: any) => {
                        this.setState((prevState) => {
                            const characteristics = { ...prevState.characteristics };
                            if (characteristicGroup.characteristicGroupCharacteristics && characteristicGroup.characteristicGroupCharacteristics.length > 0) {
                                characteristicGroup.characteristicGroupCharacteristics.forEach((c: any) => {
                                    if (c.characteristic) {
                                        characteristics[c.characteristic.id] = c.characteristic;
                                    }
                                });
                            }
                            return {
                                characteristicGroups: {
                                    ...prevState.characteristicGroups,
                                    [id]: characteristicGroup
                                },
                                characteristics: characteristics
                            };
                        });
                    });
                }
            });
        }
    }

    public render() {
        if (this.state.loading) {
            return <LoadingIndicator />;
        }

        const user = this.props.UserStore && this.props.UserStore.userInfo;
        const loggedInUserId = user ? user.id : undefined;

        if (!loggedInUserId) {
            return <div>Please log in to see the detailed preview.</div>;
        }

        if (!this.state.selectedUserData) {
            return <div>No report data found for your user. Please ensure you have completed a survey.</div>;
        }

        const surveyVersion = (this.props.FilterStore && this.props.FilterStore.surveyVersion) || this.state.selectedUserData.surveyVersion;
        const surveyTexts = this.state.surveyTexts || [];
        const aboutSurveyText = ((surveyTexts as any[]).find(st => st.surveyTextType === "individualReport") || {}).value || '';

        return (
            <div className="pdf-preview-container" style={{ padding: '20px', backgroundColor: '#f0f0f0', minHeight: '100vh' }}>
                <div style={{ marginBottom: '20px', padding: '10px', backgroundColor: '#fff', border: '1px solid #ccc' }}>
                    <strong>PDF Detailed Preview Mode</strong> (Superadmin only)
                </div>
                <div className="pdf-content-wrapper" style={{ backgroundColor: '#fff', boxShadow: '0 0 10px rgba(0,0,0,0.1)', width: '800px', margin: '0 auto' }}>
                    <PdfDetailed
                        surveyReportType={this.props.FilterStore!.surveyReportType}
                        selectedUserData={this.state.selectedUserData}
                        allStyles={this.state.allStyles}
                        aboutSurveyText={aboutSurveyText}
                        surveyVersion={surveyVersion}
                        organizationName={this.props.FilterStore && this.props.FilterStore.organization ? this.props.FilterStore.organization.name : ''}
                        notificationService={this.props.notificationService}
                        characteristicGroups={this.state.characteristicGroups}
                        characteristics={this.state.characteristics}
                        surveyReportTypes={this.state.surveyReportTypes}
                    />
                </div>
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/components/reports/individual/PdfDetailedPreview.tsx